import { Controller } from '@hotwired/stimulus'
import { variableRegex } from '../lib/vignette_variables'

// Renders the vignette scenario for whichever row the admin is working on, with
// every `{{placeholder}}` replaced by that row's resolved value.
//
// Substituted spans are tinted by where the value came from — indigo for a value
// shared by the whole group, amber for one that varies row to row — so it is
// obvious at a glance which words this row actually controls. A placeholder with
// no value stays visible as a literal `{{name}}`, because a silent gap in a
// scenario is worse than an obvious one.
//
// The `{{...}}` grammar is not defined here: the view hands over
// Vignette::VARIABLE_PATTERN.source, so the server owns the one definition.
export default class extends Controller {
  static targets = ['body', 'label']
  static values = { scenario: String, pattern: String }

  connect() {
    this.render({ label: '', values: {}, sharedKeys: [] })

    // Stimulus connects the group controller — an ancestor — first, so its
    // opening broadcast is already gone by now. Announce instead, and let it
    // re-publish the focused row.
    this.dispatch('connected', { target: document })
  }

  // Bound to the variation-group controller's focus event.
  update(event) {
    this.render(event.detail)
  }

  render({ label, values, sharedKeys }) {
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = label ? `· ${label}` : ''
    }

    const shared = new Set(sharedKeys || [])
    const resolved = values || {}
    const fragment = document.createDocumentFragment()
    const regex = variableRegex(this.patternValue)
    let cursor = 0
    let match

    while ((match = regex.exec(this.scenarioValue)) !== null) {
      fragment.append(this.scenarioValue.slice(cursor, match.index))
      cursor = match.index + match[0].length

      const name = match[1].trim()
      const value = resolved[name]

      if (value === undefined || value === '') {
        fragment.append(this.buildUnresolved(match[0]))
      } else {
        fragment.append(this.buildValue(value, shared.has(name)))
      }
    }

    fragment.append(this.scenarioValue.slice(cursor))
    this.bodyTarget.replaceChildren(fragment)
  }

  buildValue(value, isShared) {
    const span = document.createElement('span')
    span.textContent = value
    span.className = isShared
      ? 'rounded bg-indigo-50 px-1 font-medium text-indigo-800'
      : 'rounded bg-[#fef9c3] px-1 font-medium text-[#854d0e]'
    return span
  }

  buildUnresolved(token) {
    const span = document.createElement('span')
    span.textContent = token
    span.className = 'text-gray-400'
    return span
  }
}
